import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';
import { Payment, Invoice } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Payment | null>(null);
  const [formData, setFormData] = useState({
    invoiceId: '',
    amount: '',
    paymentDate: new Date(),
    paymentMethod: '',
    reference: '',
    notes: '',
  });
  const [filterInvoiceId, setFilterInvoiceId] = useState('');
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterInvoiceId) params.invoiceId = filterInvoiceId;

      const [paymentsRes, invoicesRes] = await Promise.all([
        api.get('/payments', { params }),
        api.get('/invoices'),
      ]);

      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [filterInvoiceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpen = (payment?: Payment) => {
    if (payment) {
      setEditing(payment);
      setFormData({
        invoiceId: payment.invoiceId,
        amount: payment.amount.toString(),
        paymentDate: new Date(payment.paymentDate),
        paymentMethod: payment.paymentMethod || '',
        reference: payment.reference || '',
        notes: payment.notes || '',
      });
    } else {
      setEditing(null);
      setFormData({
        invoiceId: '',
        amount: '',
        paymentDate: new Date(),
        paymentMethod: '',
        reference: '',
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        paymentDate: formData.paymentDate.toISOString(),
        amount: parseFloat(formData.amount),
      };

      if (editing) {
        await api.put(`/payments/${editing.id}`, data);
      } else {
        await api.post('/payments', data);
      }

      fetchData();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save payment');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      await api.delete(`/payments/${id}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete payment');
    }
  };

  const exportToExcel = async () => {
    try {
      const params: any = {};
      if (filterInvoiceId) params.invoiceId = filterInvoiceId;

      const response = await api.get('/export/payments', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = user?.role === 'ADMIN';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payments</Typography>
        <Box>
          <Button variant="outlined" onClick={exportToExcel} sx={{ mr: 1 }}>
            Export to Excel
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Payment
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Filter by Invoice</InputLabel>
          <Select
            value={filterInvoiceId}
            label="Filter by Invoice"
            onChange={(e) => setFilterInvoiceId(e.target.value)}
          >
            <MenuItem value="">All Invoices</MenuItem>
            {invoices.map((inv) => (
              <MenuItem key={inv.id} value={inv.id}>
                {inv.invoiceNumber} - {inv.contractor?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Payment Date</TableCell>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Contractor</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Notes</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{format(new Date(payment.paymentDate), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{payment.invoice?.invoiceNumber || '-'}</TableCell>
                <TableCell>{payment.invoice?.contractor?.name || '-'}</TableCell>
                <TableCell align="right">${payment.amount.toFixed(2)}</TableCell>
                <TableCell>{payment.paymentMethod || '-'}</TableCell>
                <TableCell>{payment.reference || '-'}</TableCell>
                <TableCell>{payment.notes || '-'}</TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(payment)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(payment.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Payment' : 'Add Payment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Invoice</InputLabel>
              <Select
                value={formData.invoiceId}
                label="Invoice"
                onChange={(e) => setFormData({ ...formData, invoiceId: e.target.value })}
                required
                disabled={!!editing}
              >
                {invoices.map((inv) => (
                  <MenuItem key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} - {inv.contractor?.name} (${inv.total.toFixed(2)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0' }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Payment Date"
                value={formData.paymentDate}
                onChange={(date) => setFormData({ ...formData, paymentDate: date || new Date() })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={formData.paymentMethod}
                label="Payment Method"
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              >
                <MenuItem value="Check">Check</MenuItem>
                <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
                <MenuItem value="ACH">ACH</MenuItem>
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Reference"
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              fullWidth
              placeholder="Check number, transaction ID, etc."
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;

